import { Component, Inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { BasicService } from 'src/app/shared/services/basic.service';

import { CountersService } from '../services/counters.service';
import { NavigationService } from '../services/navigation.service';
import { Counter, TaskListConfiguration } from '../shared/models';

/**
 * Tabs in inbox are not dedicated to contain the task list and filters.
 * They only contain second level tabs.
 * All tabs are just filters.
 */
@Component({
  selector: 'app-tabs-navigation',
  templateUrl: './tabs-navigation.component.html',
  styleUrls: ['./tabs-navigation.component.scss'],
  encapsulation: ViewEncapsulation.None, // we can't style counter in active tab without this :/
})
export class TabsNavigationComponent implements OnInit, OnDestroy {
  @Input() taskListConfiguration: TaskListConfiguration;

  stateInboxNg = 'auth.alcmeon.inbox.list.tasks';
  tabsTaskListConfigs: TaskListConfiguration[];
  counters: Counter[] = [];

  selectedTab: number;
  tabMap: Map<number, TaskListConfiguration> = new Map(); // to get parent tab config by index
  queryIdMap: Map<string, number> = new Map(); // to get parent tab number by queryId of parent or child

  private subscriptions = new Subscription();

  constructor(
    private basicService: BasicService,
    private countersService: CountersService,
    private navigationService: NavigationService,
    @Inject('$state') private $state: any,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {
    // these 2 subscribe needs to be in constructor, to be subscribed before the init of all three components : this one, ChannelsNavigationComponent, InsertsByMeComponent

    this.subscriptions.add(
      this.countersService.initCounterIndexesBS.subscribe(() => {
        this.updateCounterIndexes();
      })
    );

    this.subscriptions.add(
      this.countersService.countersBS.subscribe(counters => {
        this.getCountersCallback(counters);
      })
    );
  }

  ngOnInit(): void {
    this.tabsTaskListConfigs = this.navigationService.getAllTabsTaskListConfigs();
    this.countersService.initTabsCounters(this.tabsTaskListConfigs, this.taskListConfiguration);

    this.tabsTaskListConfigs.forEach((config: TaskListConfiguration, index: number) => {
      this.tabMap.set(index, config);

      // tab without children
      if (config.queueId) {
        this.queryIdMap.set(config.queueId, index);
      }

      config.children?.forEach((subConfig: TaskListConfiguration) => {
        // real sub tab, not just for label
        if (subConfig.queueId) {
          this.queryIdMap.set(subConfig.queueId, index);
        }
      });
    });

    this.initSelectedTab();

    this.subscriptions.add(
      this.navigationService.loadQueueBS.subscribe(queueId => {
        this.$state.go(
          this.stateInboxNg,
          {
            queueId: queueId,
            channelQueueId: this.taskListConfiguration.channelQueueId,
          },
          {
            reload: this.stateInboxNg,
          }
        );
      })
    );
  }

  private initSelectedTab() {
    this.selectedTab = this.queryIdMap.get(this.taskListConfiguration.queueId);
  }

  /**
   * When changing a tab, load the queueId of the tab if it hasn't any children.
   * Or load the queueId of its default children.
   */
  changeTab(tabIndex: number) {
    const config = this.tabMap.get(tabIndex);

    if (config.parameters) {
      this.loadQueue(config.queueId);
    } else {
      const defaultSubTab = config.children.find(child => child.isSelected);
      const subTabQueueId = defaultSubTab ? defaultSubTab.queueId : config.children[0].queueId;
      this.loadQueue(subTabQueueId);
    }
  }

  changeSubTab(queueId: string) {
    this.loadQueue(queueId);
  }

  /**
   * Allows to load the given queue through the mecanism that wait 300ms between clicks to avoid HTTP errors 429 with the `crazies click monkeys`.
   * @param queueId
   */
  private loadQueue(queueId: string) {
    this.navigationService.loadQueueBS.next(queueId);
  }

  private getCountersCallback(counters: Counter[]) {
    this.updateCountersForDisplay(counters);

    const numberOfPendingTasks = this.countersService.getNumberOfPendingTasks();
    this.topToolbarService.setPendingMessagesCounter(numberOfPendingTasks);
  }

  /**
   * Inject counter indexes in tabsTaskListConfigs.
   * The goal is to know if a counter will be displayed or not, before countersBS is notified.
   * So we display a loading counter or nothing.
   */
  private updateCounterIndexes() {
    let counterIndex = 0;
    this.tabsTaskListConfigs.forEach(config => {
      config.counterIndex = this.countersService.tabsCounterIndexes[counterIndex];
      counterIndex++;

      config.children?.forEach(child => {
        child.counterIndex = this.countersService.tabsCounterIndexes[counterIndex];
        counterIndex++;
      });
    });
  }

  /**
   * Inject counterIndex and counters in tabsTaskListConfigs
   * There is one counter that is calculated by front
   */
  private updateCountersForDisplay(counters: Counter[]) {
    if (counters?.length) {
      this.counters = counters;
      this.tabsTaskListConfigs.forEach(config => {
        if (config.counterIndex >= 0 && this.counters[config.counterIndex]) {
          config.counter = [this.counters[config.counterIndex].counter];
        }

        config.children?.forEach((child, index) => {
          if (child.counterIndex >= 0 && this.counters[child.counterIndex]) {
            child.counter = [this.counters[child.counterIndex].counter];

            // -1 is a flag to calculate counters in relation to children, only used for ongoing tab
            if (config.counterIndex === -1) {
              if (index === 0) {
                config.counter = [...child.counter, 0];
                config.counterTooltip = [this.basicService.translate('inbox/tab/by-me')];
              } else {
                config.counter[1] += child.counter[0];
                config.counterTooltip[1] = this.basicService.translate('inbox/tab/by-others');
              }
            }
          }
        });
      });
    }
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
  }
}
